import { useEffect, useRef } from 'react';

import { useAppDispatch } from 'Store';

import {
  getCourseDesktops,
  getMyDesktop,
  useSelectCourses,
  useSelectCoursesError,
  useSelectTestCourse
} from '../../../data';

export const useCoursesLoader = () => {
  const dispatch = useAppDispatch();
  const courses = useSelectCourses();
  const testCourse = useSelectTestCourse();
  const coursesError = useSelectCoursesError();

  const requestedCoursesRef = useRef<Set<string>>(new Set());
  const abortControllersRef = useRef<Map<string, AbortController>>(new Map());
  const previousCoursesLengthRef = useRef<number>(0);

  const cancelAllDesktopRequests = () => {
    abortControllersRef.current.forEach((controller, key) => {
      controller.abort();
    });
    abortControllersRef.current.clear();
    requestedCoursesRef.current.clear();
  };

  useEffect(() => {
    const currentLength = courses?.length || 0;
    if (Math.abs(currentLength - previousCoursesLengthRef.current) > 1) {
      cancelAllDesktopRequests();
    }

    previousCoursesLengthRef.current = currentLength;

    if (courses?.length > 0) {
      const coursesToLoad = courses.filter(course => {
        const hasMyDesktop = course.myDesktop !== undefined;
        const hasTrainerDesktop = course.trainerDesktop !== undefined;
        const isLoadingDesktops =
          Boolean(course.isMyDesktopLoading) || Boolean(course.isTrainerDesktopLoading);
        const alreadyRequested = requestedCoursesRef.current.has(course.id);

        return !hasMyDesktop && !hasTrainerDesktop && !isLoadingDesktops && !alreadyRequested;
      });

      coursesToLoad.forEach(course => {
        requestedCoursesRef.current.add(course.id);

        const controller = new AbortController();
        abortControllersRef.current.set(course.id, controller);

        dispatch(getCourseDesktops({ courseId: course.id, signal: controller.signal }));
      });
    }
  }, [dispatch, courses]);

  useEffect(() => {
    if (testCourse?.id) {
      const hasMyDesktop = testCourse.myDesktop !== undefined;
      const isLoadingMyDesktop = testCourse.isMyDesktopLoading;
      const alreadyRequested = requestedCoursesRef.current.has(`myDesktop-${testCourse.id}`);

      if (!hasMyDesktop && !isLoadingMyDesktop && !alreadyRequested) {
        requestedCoursesRef.current.add(`myDesktop-${testCourse.id}`);

        const controller = new AbortController();
        abortControllersRef.current.set(`myDesktop-${testCourse.id}`, controller);

        dispatch(getMyDesktop({ courseId: testCourse.id, signal: controller.signal }));
      }
    }
  }, [dispatch, testCourse?.id, testCourse?.isMyDesktopLoading, testCourse?.myDesktop]);

  useEffect(
    () => () => {
      cancelAllDesktopRequests();
    },
    []
  );

  return {
    loadError: coursesError,
    cancelAllDesktopRequests
  };
};
